import * as React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import { formatSeperatedInteger } from './utils/format';
import { IInstantOfferDetails } from './InstantOffers';
import { buildScheduleEligibleOfferLink } from './utils/constants';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface IEligibleOfferProps {
    offerAmount: number;
    expirationDateUtc: string;
    expirationDisplayDate: string;
    offerCode: string;
    storeId: string;
    instantOfferDetails: IInstantOfferDetails;
}

function EligibleOffer(props: IEligibleOfferProps) {
    dayjs.extend(customParseFormat);
    dayjs.extend(duration);

    const calculateDurationToExpiration = () => {
        return dayjs.duration(Math.max(0, expirationDate.diff(dayjs())));
    };

    const expirationDate = dayjs(props.expirationDisplayDate || props.expirationDateUtc)
        .hour(23)
        .minute(59)
        .second(59);
    const [durationToExpiration, setDurationToExpiration] = React.useState(calculateDurationToExpiration());

    React.useEffect(() => {
        const cleanup = () => {
            clearInterval(durationCalcInterval);
        };

        const durationCalcInterval = setInterval(() => {
            const timeToExpiration = calculateDurationToExpiration();

            setDurationToExpiration(timeToExpiration);

            if (timeToExpiration.asSeconds() <= 0) {
                cleanup();
            }
        }, 10000);

        return cleanup;
    }, []);

    const isExpired = () => {
        return durationToExpiration.asSeconds() <= 0;
    };

    const vehicleMakeAndModel = () => {
        return props.instantOfferDetails.vehicleMake && props.instantOfferDetails.vehicleModel
            ? `${props.instantOfferDetails.vehicleMake} ${props.instantOfferDetails.vehicleModel}`
            : 'vehicle';
    };

    const title = isExpired() ? (
        <Typography component="h2" variant="h5">
            It&apos;s time to update your offer
        </Typography>
    ) : (
        <Typography component="h2" sx={{ typography: { xs: 'h6', sm: 'h5' } }}>
            Your ${formatSeperatedInteger(props.offerAmount)}
            <sup>&dagger;</sup> offer on your {vehicleMakeAndModel()} expires soon
        </Typography>
    );

    if (durationToExpiration == null) return null;

    return (
        <Paper elevation={2} sx={{ borderRadius: 2, maxWidth: { sm: '20.5rem', md: '24rem' } }}>
            <Box sx={{ p: 4 }}>
                {isExpired() ? (
                    <Button variant="contained" onClick={props.instantOfferDetails.onStartNewOffer}>
                        Get started
                    </Button>
                ) : (
                    <>
                        <Box sx={{ mb: 3 }}>{title}</Box>
                        <Stack spacing={1}>
                            <Button
                                variant="contained"
                                href={buildScheduleEligibleOfferLink(props.storeId, props.offerCode)}
                                target="_blank"
                                fullWidth
                            >
                                Set my appointment
                            </Button>
                            <Button
                                variant="outlined"
                                type="button"
                                onClick={props.instantOfferDetails.onStartNewOffer}
                                fullWidth
                            >
                                Start a new offer
                            </Button>
                        </Stack>
                    </>
                )}
            </Box>
            <Divider />
            <Box
                sx={{
                    px: 3,
                    py: 1,
                }}
            >
                <Typography component="p" variant="caption" align="center" color="text.secondary">
                    {isExpired() ? (
                        `Your offer expired on ${expirationDate.format('MM/DD/YYYY')}`
                    ) : (
                        <>
                            Valid until {expirationDate.format('MM/DD/YYYY')}
                            <Typography component="span">&nbsp;|&nbsp;</Typography>Offer Code: {props.offerCode}
                        </>
                    )}
                </Typography>
            </Box>
        </Paper>
    );
}

export default EligibleOffer;
